import { useRef } from 'react';

interface EmptySearchResultProps {
  type: 'image' | 'text';
  onBack: () => void;
  onImageUpload?: (file: File) => void;
}

export default function EmptySearchResult({
  type,
  onBack,
  onImageUpload,
}: EmptySearchResultProps) {
  const isImageSearch = type === 'image';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleRetryClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) {
      onImageUpload(file);
    }
    e.target.value = '';
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      {/* 隐藏的文件输入 */}
      {isImageSearch && onImageUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      )}

      {/* 图标 */}
      <div className="w-24 h-24 rounded-full bg-background-secondary flex items-center justify-center mb-6 relative">
        {isImageSearch ? (
          // 图片搜索图标
          <svg
            className="w-12 h-12 text-secondary-light"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        ) : (
          // 文字搜索图标
          <svg
            className="w-12 h-12 text-secondary-light"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        )}
        {/* 问号角标 */}
        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
          <span className="text-white text-lg font-bold">?</span>
        </div>
      </div>

      {/* 标题 */}
      <h3 className="text-xl font-semibold text-primary mb-2">
        {isImageSearch ? '未找到匹配的卡片' : '未找到相关卡片'}
      </h3>

      {/* 描述 */}
      <p className="text-secondary text-center max-w-sm mb-8">
        {isImageSearch
          ? '换一张清晰的卡片图片试试，确保图片中的卡片完整可见'
          : '换一个关键词试试，或者尝试使用图片搜索'}
      </p>

      {/* 操作按钮 */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="px-6 py-2.5 bg-white border border-border text-primary text-sm rounded-full hover:bg-background-secondary transition-colors font-medium"
        >
          返回首页
        </button>
        {isImageSearch && onImageUpload && (
          <button
            onClick={handleRetryClick}
            className="px-6 py-2.5 bg-primary text-white text-sm rounded-full hover:bg-primary-light transition-colors font-medium"
          >
            重新上传图片
          </button>
        )}
      </div>
    </div>
  );
}
